/**
 * LoginForm Component
 *
 * Description:
 * Handles user authentication for the platform. Renders a login form, manages input state, validates user existence, and processes login requests. Integrates with translation, routing, and Redux actions for a seamless login experience.
 *
 * Features:
 * - Dynamic rendering of input fields based on configuration.
 * - Password visibility toggle for better UX.
 * - Checks if the user exists before allowing password entry.
 * - Handles login requests and displays loading states.
 * - Shows error messages for failed login attempts.
 * - Integrates with routing for account recovery and registration.
 * - Uses translations for multi-language support.
 *
 * Props:
 * (No props required. All state is managed internally.)
 *
 * Hooks & State:
 * - useTranslation: Provides translation for UI text.
 * - useState:
 *   - showPassword: Controls password visibility toggle.
 *   - errorLogin: Tracks login error state for display.
 *   - existUser: Tracks if the user exists in the system (true/false/'noUser').
 *   - sendData: Indicates if login request is in progress.
 * - useForm: Manages form state and validation with register, handleSubmit, and watch.
 * - useEffect: Resets error and user existence states when input changes.
 *
 * Render Flow:
 * 1. Renders input fields for username and password based on configuration.
 * 2. Checks if the user exists after username input on blur.
 * 3. If user exists, shows password field and enables login button.
 * 4. Handles login submission and shows loading spinner during request.
 * 5. Displays error messages or redirects to account recovery/registration as needed.
 *
 * Good Practices / Warnings:
 * - Avoid storing sensitive data in state.
 * - Use translation hooks for all user-facing text.
 * - Ensure form validation and error handling for better UX.
 * - Use routing for navigation to account recovery and registration.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Button from '@mui/material/Button';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Loading from 'components/UI/Loading/Loading';

import { getLoginFormData } from '../../constants/LoginForm';
import { getUserAccount, logIn } from 'store/user-actions';

import ErrorMessage from 'components/UI/ErrorMessage/ErrorMessage';

import classes from './LoginForm.module.css';

const LoginForm = () => {
  const { t } = useTranslation(['login']);

  // State for password visibility toggle
  const [showPassword, setShowPassword] = useState(false);

  // State for login error handling
  const [errorLogin, setErrorLogin] = useState(false);

  // Form management hook
  const { register, handleSubmit, watch } = useForm();

  // State for user existence validation
  const [existUser, setExistUser] = useState(false);

  // State for loading during login request
  const [sendData, setSendData] = useState(false);

  // Watch form inputs for changes
  const [userText, passwordText] = watch([
    'userText',
    'passwordText'
  ]);

  // Reset error state when input changes
  useEffect(() => {
    setErrorLogin(false);
  }, [userText, passwordText]);

  // Reset user existence state when username changes
  useEffect(() => {
    setExistUser(false);
  }, [userText]);

  /**
   * Toggles password visibility
   */
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  /**
   * Handles form submission and login process
   */
  const onSubmit = async data => {
    setSendData(true);
    const loginUser = await logIn(data.userText, data.passwordText);
    if (loginUser) {
      setSendData(false);
    }
    setErrorLogin(loginUser);
  };

  /**
   * Checks if user exists in the system
   */
  const checkUser = async () => {
    const checkUserData = await getUserAccount(userText);
    if (checkUserData) {
      setExistUser(true);
    } else {
      setExistUser('noUser');
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={classes.loginForm}
    >
      {getLoginFormData().map(input =>
        input.type === 'password' ? (
          <>
            {existUser === true && (
              <OutlinedInput
                key={input.id}
                className={classes.textField}
                {...register(`${input.id}`)}
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowPassword} // Toggle password visibility
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                placeholder={t(input.text)}
              />
            )}
          </>
        ) : (
          <>
            <OutlinedInput
              key={input.id}
              className={classes.textField}
              {...register(`${input.id}`)}
              placeholder={t(input.text)}
              onBlur={checkUser} // Check user existence on blur
            />
            {existUser === true && (
              <p className={classes.coplementaryText}>
                {t('exist_account')}
              </p>
            )}
            {!existUser && (
              <Button
                onClick={checkUser} // Check user existence on click
                className={classes.checkUserBtn}
                variant="contained"
              >
                {t('continue')}
              </Button>
            )}
          </>
        )
      )}
      {existUser === true && (
        <>
          <div className={classes.btnContainer}>
            {!sendData ? (
              <Button className={classes.btnSend} type="submit">
                {t('continue')}
              </Button>
            ) : (
              <Loading /> // Loading component during request
            )}
          </div>
          <div className={classes.btnContainer}>
            <Button
              className={classes.recoverAccountBtn}
              component={Link}
              to="/recoverAccount" // Navigate to account recovery
            >
              {t('forgot_password')}
            </Button>
          </div>
        </>
      )}
      {existUser === 'noUser' && (
        <>
          <p className={classes.coplementaryText}>
            {t('not_account')}
          </p>
          <div className={classes.btnContainer}>
            <Button
              component={Link}
              to="/register" // Navigate to registration
              className={classes.btnSend}
            >
              {t('create_account')}
            </Button>
          </div>
        </>
      )}
      {errorLogin && (
        <ErrorMessage
          open={errorLogin} // Error state control
          message={t('username_and_password_not_match')} // Error message
        />
      )}
    </form>
  );
};

export default LoginForm;
